import React, { FC } from 'react'
import { useEffect } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import FhirLoader from '../../FhirLoader'
import { useParams } from 'react-router-dom'
import useAccount from '../../../hooks/admin/useAccount'
import {formatDateTime, scrollToTop } from '../../../utils/helpers'
import isAuthenticated from '../../../routes/PrivateRoute'
import useSaveAccount from '../../../hooks/admin/useSaveAccount'
import useSendForgotEmail from '../../../hooks/admin/useSendForgotEmail'
import PermissionGate from '../../PermissionGate'
import useUserWithRole from '../../../hooks/authentication/useUserWithRole'
import Identifier from '../../FhirData/DataTypes/Identifier'
import useAlert from '../../../hooks/authentication/useAlert'
import PageTitle from '../../PageTitle'
import { appConfig } from '../../../assets/customizations/config'
import useEnvironment from '../../../hooks/location/useEnviroment'

interface RouteMatchParams {
  accountId: string;
}

const Account: FC = () => {
  const { alert, handleCloseAlert, showAlert } = useAlert({
    show: false,
    variant: 'danger',
    message: '',
  })

  const { accountId } = useParams<RouteMatchParams>()
  const {
    data: account,
    refetch: refetchAccount,
    isLoading: isAccountLoading,
    isSuccess: isAccountSuccess,
  } = useAccount(accountId)

  const updateAccount = useSaveAccount(accountId)
  const { user } = useUserWithRole()

  useEffect(() => {
    refetchAccount()
  }, [])

  useEffect(() => {
    if (updateAccount.isSuccess) {
      showAlert('success', 'Successfully updated account.')
      scrollToTop()
    }
  }, [updateAccount.isSuccess])

  useEffect(() => {
    if (updateAccount.isError) {
      showAlert('danger', 'Unable to update account: ')

      scrollToTop()
    }
  }, [updateAccount.isError])

  const handleUpdateAccount = () => {
    updateAccount.mutate({
      enabled: !account.enabled,
    })
  }

  const forgotLogin = useSendForgotEmail()
  const handlePasswordReset = () => {
    forgotLogin.mutate({
      email: account.email,
      type: 'Password',
      memberId: account?.attributes
        ? account?.attributes.memberId[0]
        : account?.memberId,
      id: accountId,
    })
  }

  useEffect(() => {
    if (forgotLogin.isSuccess) {
      showAlert('success', 'Successfully requested user to reset password.')
    }

    if (forgotLogin.isError) {
      showAlert('danger', 'Unable to send Password Reset: ')
    }
  }, [forgotLogin.isSuccess, forgotLogin.isError])

  const { data: environment } = useEnvironment()
  return (
    <PermissionGate user={user} role="admin">
      <section className='content'>
        <Container>
          <PageTitle displayName="Member Account" />
          <Row>
            <Col sm={6}>
              <h1 className="headerText h2 mb-0">Member Account</h1>
            </Col>
            <Col sm={12}>
              <Alert
                show={alert.show}
                variant={alert.variant}
                dismissible
                onClose={() => handleCloseAlert()}
              >
                {alert.message}
                {updateAccount.isError && (
                  <div>{updateAccount.error?.response?.text ?? ''}</div>
                )}
              </Alert>
              {
                isAccountLoading && (
                  <FhirLoader message='Account Loading...' small={true} />
                )}
              {
                isAccountSuccess && (
                  <section className="dataContainer">
                    <h2 className='mb-4 h5'><strong>ACCOUNT DETAILS</strong></h2>
                    <dl>
                      <Row>
                        <Col sm={3}>
                          <dt>Username</dt>
                        </Col>
                        <Col sm={9}>
                          <dd>{account?.username}</dd>
                        </Col>
                        <Col sm={3}>
                          <dt>Email</dt>
                        </Col>
                        <Col sm={9}>
                          <dd>{account?.email}</dd>
                        </Col>
                        <Col sm={3}>
                          <dt>Patient Identifier</dt>
                        </Col>
                        <Col sm={9}>
                          <dd>
                            <Identifier data={account.attributes.memberId} />
                          </dd>
                        </Col>
                        <Col sm={3}>
                          <dt>Proxy</dt>
                        </Col>
                        <Col sm={9}>
                          <dd>{account?.isProxy ? 'Yes' : ''}</dd>
                        </Col>
                        <Col sm={3}>
                          <dt>Proxy Name</dt>
                        </Col>
                        <Col sm={9}>
                          <dd>
                            {account?.proxyFirstName || ''}{''}
                            {account?.proxyLastName || ''}
                          </dd>
                        </Col>
                        <Col sm={3}>
                          <dt>Last Login</dt>
                        </Col>
                        <Col sm={9}>
                          <dd>
                            {account?.lastLoginDate
                              ? formatDateTime(account?.lastLoginDate)
                              : ''}
                          </dd>
                        </Col>
                        <Col sm={3}>
                          <dt>Account Status</dt>
                        </Col>
                        <Col sm={9}>
                          <dd>{account?.enabled ? 'Active' : 'Locked'}</dd>
                        </Col>
                      </Row>
                      <h3 className="mt-4 h5">
                        <strong>LOCK or UNLOCK ACCOUNT</strong>
                      </h3>
                      <p>
                        If an account is LOCKED, a member will be unable to log
                        into the portal and any third party apps that have been
                        authorized, will not be able to connect. If a member has
                        more than 5 login attempts, their account will be locked.
                      </p>
                      <Button
                        variant={account?.enabled ? 'danger' : 'success'}
                        className="mb-4"
                        onClick={handleUpdateAccount}
                        disabled={updateAccount.isLoading}
                      >
                        {
                          updateAccount.isLoading ? (
                            <FhirLoader small={true} />
                          ) : account?.enabled ? (
                            'Lock Account'
                          ) : (
                            'Unlock Account'
                          )}
                      </Button>
                     {environment?.showAdminPasswordReset && (
                        <>
                          <h3 className='h5'>
                            <strong>PASSWORD RESET</strong>
                          </h3>

                          <p>
                            A member can use self-service password reset on the Login
                            Page, or a reset email can be sent to the account email
                            address using the button below:
                          </p>

                          <Button
                            variant='primary'
                            onClick={handlePasswordReset}
                            disabled={forgotLogin.isLoading}
                          >
                            {forgotLogin.isLoading ? (
                              <FhirLoader small={true} />
                            ) : (
                              'Send Email'
                            )}
                          </Button>
                        </>
                      )}
                    </dl>
                  </section>
                )}
            </Col>
          </Row>
        </Container>
      </section>
    </PermissionGate>
  )
}

export default isAuthenticated(Account)
